import React, { useMemo, useCallback } from "react";
import PageLoader from "../ui/PageLoader";
import "../../assets/css/style.css";
import { useGetUserQuery } from "../../apis/user";
import { useGetCompaniesQuery } from "../../apis/company";
import Table from "components/ui/Table";

function escapeCsvValue(value) {
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadCsv(headers, rows, filename) {
  const csvContent = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const SocialSubscription = () => {
  const pageSize = 10;

  const { data: allUsers = [], isLoading: isUsersLoading } = useGetUserQuery();
  const { data: allCompanies = [], isLoading: isCompaniesLoading } =
    useGetCompaniesQuery();

  const companiesMap = useMemo(() => {
    const map = new Map();
    allCompanies.forEach((company) => {
      map.set(company._id, company);
    });
    return map;
  }, [allCompanies]);

  const data = useMemo(() => {
    return (allUsers || [])
      .filter((user) => user.isSubscribed)
      .map((user) => {
        const company = user.companyId
          ? companiesMap.get(user.companyId)
          : null;
        return {
          ...user,
          companyName: company?.name || company?.companyName || "N/A",
        };
      });
  }, [allUsers, companiesMap]);

  const handleExport = useCallback(() => {
    if (!data || data.length === 0) return;

    const headers = [
      "#",
      "Name",
      "Email",
      "Mobile",
      "Company",
      "Subscription Status",
    ];

    const csvRows = data.map((row, index) => [
      index + 1,
      row.name || "",
      row.email || "",
      row.mobile || "",
      row.companyName || "N/A",
      row.isSubscribed ? "Active" : "None",
    ]);

    downloadCsv(headers, csvRows, "social-subscriptions");
  }, [data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      { accessorKey: "name", header: "Name", isFilter: true },
      { accessorKey: "email", header: "Email", isFilter: true },
      { accessorKey: "mobile", header: "Mobile", isFilter: true },
      {
        accessorKey: "companyName",
        header: "Company",
        cell: (row) => row.companyName || "N/A",
        isFilter: true,
      },
      {
        accessorKey: "isSubscribed.status",
        header: "Subscription Status",
        cell: (row) => (row.isSubscribed ? "Active" : "None"),
        isFilter: true,
      },
    ],
    []
  );

  const isLoading = isUsersLoading || isCompaniesLoading;

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <div className="container mx-auto">
      <Table
        title=""
        columns={columns}
        data={data || []}
        pageSize={pageSize}
        ButtonCmp={() => (
          <button onClick={handleExport} className="black-button">
            Export
          </button>
        )}
      />
    </div>
  );
};

export default SocialSubscription;
